import { format } from 'date-fns';
import React from 'react';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { getExportFileName } from 'src/utils/export-image-frame';

interface TitleBlockProps {
  scale: number;
  border: string;
}
const CELL_PADDING_V = 2;
const CELL_PADDING_H = 4;
const CELL_PADDING_RIGHT = 8; // Extra right padding so PDF export doesn't clip last chars

function TitleBlock({ scale, border }: TitleBlockProps) {
  const { projectData } = useGlobalContext();
  const projectName = projectData?.projectName ?? 'network-configuration';
  const revision = projectData?.revisionNumber ?? '';
  const fileName = getExportFileName(projectName, revision);

  const pad = {
    top: CELL_PADDING_V * scale,
    right: CELL_PADDING_RIGHT * scale,
    bottom: CELL_PADDING_V * scale,
    left: CELL_PADDING_H * scale,
  };

  return (
    <div
      className="absolute bg-white"
      style={{
        bottom: 0,
        right: 0,
        width: `${450 * scale}px`,
        borderLeft: border,
        borderTop: border,
        overflow: 'visible',
      }}
    >
      <div style={{ overflow: 'visible' }}>
        <table
          className="box-border w-full"
          style={{ fontSize: `${8 * scale}px` }}
        >
          <tbody>
            <tr>
              <td
                colSpan={6}
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderBottom: border,
                }}
              >
                <div className="flex flex-col">
                  <span>
                    <strong>{projectData.companyName}</strong>
                  </span>
                  <span>
                    <strong>{projectData.companyAddress}</strong>
                  </span>
                </div>
              </td>
            </tr>
            <tr>
              <td
                colSpan={6}
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderBottom: border,
                }}
              >
                <div className="flex flex-col">
                  <span>
                    Project:{' '}
                    <strong>
                      {projectData.projectName}{' '}
                      {projectData.projectNumber
                        ? `(${projectData.projectNumber})`
                        : ''}
                    </strong>
                  </span>
                  <span>
                    Drawn by: <strong>{projectData.drawnBy}</strong>
                  </span>
                  {projectData.responsibleEngineer && (
                    <span>
                      Responsible engineer:{' '}
                      <strong>{projectData.responsibleEngineer}</strong>
                    </span>
                  )}
                  {projectData.description && (
                    <span>
                      Description: <strong>{projectData.description}</strong>
                    </span>
                  )}
                </div>
              </td>
            </tr>
            <tr>
              <td
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderRight: border,
                  borderBottom: border,
                  width: `${50 * scale}px`,
                }}
              >
                <span>
                  Size: <strong>{projectData.exportSettings.paperSize}</strong>
                </span>
              </td>
              <td
                colSpan={4}
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderRight: border,
                  borderBottom: border,
                }}
              >
                <span>
                  File name: <strong>{fileName}</strong>
                </span>
              </td>
              <td
                colSpan={1}
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderBottom: border,
                }}
              >
                <span>
                  Rev. <strong>{revision}</strong>
                </span>
              </td>
            </tr>
            <tr>
              <td
                colSpan={3}
                className="text-left"
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                  borderRight: border,
                }}
              >
                <span>
                  Date: <strong>{format(new Date(), 'yyyy-MM-dd')}</strong>
                </span>
              </td>
              <td
                colSpan={3}
                style={{
                  padding: `${pad.top}px ${pad.right}px ${pad.bottom}px ${pad.left}px`,
                }}
              >
                <span>
                  Sheet <strong>1</strong> of <strong>1</strong>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(TitleBlock);
